import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatInstant } from '@/lib/dates';
import {
  DELIVERY_STATUS_LABELS,
  MATCH_METHOD_LABELS,
  type ConversationMessageView,
} from '@shared/schemas/conversation';

/*
 * What a message actually arrived with — owner request, 2026-08-08.
 *
 * > *"The individual messages that come in should have the option to view the
 * > full message details as a small link below the message, useful for checking
 * > the headers for anything malicious."*
 *
 * **Closed by default and small when closed**, because that is the whole point
 * of the change it belongs to: the thread reads like a conversation, and the
 * email machinery is one click away rather than stacked on top of every line
 * somebody wrote. Before this, the sender's address, the provenance chip and
 * the delivery status sat on every message and made a chat look like a ticket
 * queue.
 *
 * **Two layers, and the second is fetched only when asked for.** What the
 * application already knows — who it says it is from, who it was addressed to,
 * how it was matched, what happened to it — comes down with the thread and
 * costs nothing. The raw headers are a request to the server, made when
 * somebody opens the panel, because most messages are never questioned.
 */

/** The header block, fetched on demand. Never the body — see the route. */
function useMessageSource(conversationId: string, messageId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'conversations', conversationId, 'source', messageId],
    queryFn: () =>
      api.getText(`/api/admin/conversations/${conversationId}/messages/${messageId}/source`),
    enabled,
    // The original never changes once stored, so there is nothing to refetch.
    staleTime: Infinity,
  });
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2 py-0.5">
      <dt className="text-caption w-28 shrink-0 text-ink-500">{label}</dt>
      <dd className="text-caption min-w-0 flex-1 break-all text-ink-900">{children}</dd>
    </div>
  );
}

export function MessageDetails({
  conversationId,
  message,
  mine,
  meta,
}: {
  conversationId: string;
  message: ConversationMessageView;
  /** Ours, so the row aligns to the same side as the bubble above it. */
  mine: boolean;
  /**
   * The time and delivery status, when this message ends a run.
   *
   * **Passed in rather than rendered here** so there is one row under a bubble
   * instead of two. The toggle sits on every message — the owner asked for the
   * details of *"the individual messages"*, and a link that only appeared on
   * the last of a run would leave the middle of somebody's three-line question
   * with no way to check where it came from.
   */
  meta?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  /*
   * **Only fetched once the panel is open**, and it stays fetched after it is
   * closed again — reopening the same message should not go back to the server
   * for a file that cannot have changed.
   */
  const source = useMessageSource(conversationId, message.id, open && message.hasSource);

  const inbound = message.direction === 'inbound';

  return (
    <div className="w-full">
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-2',
          mine ? 'justify-end' : 'justify-start',
        )}
      >
        {meta}
        <button
          type="button"
          onClick={() => {
            setOpen(!open);
          }}
          aria-expanded={open}
          aria-label={open ? 'Hide message details' : 'Show message details'}
          /*
           * **Small to read, big to hit** — CLAUDE.md's P23/P24 rule, in its
           * exact words: keep the visual size, expand the hit area.
           *
           * The negative margin is what makes that literal here. A 44px-tall
           * link in the row between two bubbles pushed a run apart until it
           * stopped reading as one turn, which is the whole thing this layout
           * was rebuilt for. `-my-2` lets the target overflow into the gap
           * above and below without taking any layout height, so the hit area
           * is the full 44 and the run stays tight. Nothing it overlaps is
           * clickable.
           */
          className="text-caption -my-2 inline-flex min-h-11 items-center px-1 text-ink-500 underline underline-offset-2 hover:text-bark-700"
        >
          {open ? 'Hide details' : 'Details'}
        </button>
      </div>

      {open ? (
        /*
         * **Full width, below the row.** It was inside the flex row in the
         * first version, which put a header block beside a timestamp and pushed
         * the bubble's alignment about. Found by looking at the screen.
         */
        <dl className="border-ink-200 mt-1 w-full rounded-sm border bg-white p-3 text-left">
          <Row label="From">
            {message.fromName === null
              ? message.fromAddress
              : `${message.fromName} <${message.fromAddress}>`}
          </Row>
          {message.toAddresses === null ? null : <Row label="To">{message.toAddresses}</Row>}
          {message.ccAddresses === null ? null : <Row label="Cc">{message.ccAddresses}</Row>}
          <Row label="When">{formatInstant(message.createdAt)}</Row>

          {/*
            **How it got onto this thread, in words.** A message matched by its
            reply token came back down an address only this customer was ever
            given; one filed by hand says nothing about who sent it. That is the
            first thing to look at when a message seems off, which is why it is
            here rather than only as a chip on the bubble.
          */}
          {inbound && message.matchMethod !== null ? (
            <Row label="Matched by">{MATCH_METHOD_LABELS[message.matchMethod]}</Row>
          ) : null}

          {inbound ? null : (
            <Row label="Delivery">
              {DELIVERY_STATUS_LABELS[message.deliveryStatus]}
              {message.deliveryDetail === null ? '' : ` — ${message.deliveryDetail}`}
            </Row>
          )}

          {message.messageIdHeader === null ? null : (
            <Row label="Message-ID">{message.messageIdHeader}</Row>
          )}
          {message.inReplyTo === null ? null : <Row label="In-Reply-To">{message.inReplyTo}</Row>}

          {message.hasSource ? (
            <div className="border-ink-200 mt-2 border-t pt-2">
              <p className="text-caption text-ink-500">
                The headers it arrived with. The <span className="font-medium">Received</span> lines
                are the machines that actually handled it — a message claiming to be from somebody
                it is not will usually disagree with itself here.
              </p>

              {source.isPending ? (
                <div className="mt-2 h-16 animate-pulse rounded-sm bg-ink-100" aria-busy="true" />
              ) : source.isError ? (
                // A failed request is an error, never an empty state (P22).
                <p role="alert" className="text-caption mt-2 text-status-priority-text">
                  The original could not be read.{' '}
                  <button
                    type="button"
                    onClick={() => void source.refetch()}
                    className="underline underline-offset-2"
                  >
                    Try again
                  </button>
                </p>
              ) : (
                /*
                 * **Plain text in a `<pre>`, never markup.** These bytes came
                 * from a stranger. React escapes what it renders, the route
                 * serves `text/plain` with `nosniff`, and nothing here is
                 * given to a parser — the body, which is the part that would
                 * be worth attacking through, is not served at all.
                 */
                <pre className="text-caption mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-sm bg-ink-50 p-2 text-ink-700">
                  {source.data}
                </pre>
              )}
            </div>
          ) : (
            <p className="text-caption border-ink-200 mt-2 border-t pt-2 text-ink-500">
              {/*
                Said rather than left blank. "No headers" on a message the office
                typed is not a gap, and a missing explanation reads as one.
              */}
              {inbound
                ? 'This did not arrive by email, so there are no mail headers — it came through a form on the website.'
                : 'This went out from here, so there are no incoming headers to check.'}
            </p>
          )}
        </dl>
      ) : null}
    </div>
  );
}
